import { bpHistory } from 'global/bpHistory';
import { autorun, makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { EnrollmentFlowDomainParams } from 'store/flows/enrollment/enrollmentFlowPage';
import { getFormInstances, requestSetAnswers } from 'api/operations/formService';
import { FormInstanceType, FormType } from 'types/formService';
import { requestApplyUserAction } from 'api/operations/enrollmentService';
import { USER_ACTIONS } from 'types/enrollmentService';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { FormObjectFromJSON, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { CustomFieldControl, DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getRootStoreInstance } from 'store/rootStore';
import { SelectProducerDelegate } from 'store/modules/pick-producer/selectProducerDelegate';
import {
  DDARAuthorizedAgentsDelegate,
  DDARAuthorizedAgent,
} from 'store/modules/dynamic-form/DDAR/authorizedAgentsDelegate';
import {
  DDARAdditionalContactsInformationDelegate,
  DDARAdditionalContactInformation,
} from 'store/modules/dynamic-form/DDAR/additionalContactsInformationDelegate';

export class DefaultBrokerPageStore {
  private _rootStore = getRootStoreInstance();
  private _domainParams: EnrollmentFlowDomainParams;
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();
  private _formInstance: FormInstanceType | null = null;

  public selectProducerDelegate = new SelectProducerDelegate();

  public producerDetailsForm = new DynamicFormDelegate<{
    ddarAuthorizedAgents: CustomFieldControl<DDARAuthorizedAgent[]>;
    ddarAdditionalContactsInformation: CustomFieldControl<DDARAdditionalContactInformation[]>;
  }>({
    currentUserRoles: this._rootStore.userStore.userRoles,
    fetchForm: (): Promise<FormObjectFromJSON> => Promise.resolve(this._formInstance!.form),
    onSubmit: this.onComplete.bind(this),
  });

  // Custom component delegates
  public ddarAuthorizedAgentsDelegate: DDARAuthorizedAgentsDelegate | null = null;
  public ddarAdditionalContactsInformationDelegate: DDARAdditionalContactsInformationDelegate | null = null;

  public get canCompleteStep(): boolean {
    return (
      this.selectProducerDelegate.isFormValid &&
      this.producerDetailsForm.isFormValid &&
      !this.producerDetailsForm.formController?.isLoading
    );
  }

  private _setFormInstance(formInstance: FormInstanceType): void {
    this._formInstance = formInstance;
  }

  private _resetAndPopulateProducerDetailsForm(selectedProducerId?: string): void {
    this.producerDetailsForm.formController?.resetForm();

    const selectedProducer = this.selectProducerDelegate.selectedProducer;

    const agencyName =
      selectedProducer?.sponsorSpecificData?.agencyName || this._rootStore.userStore.userInfo.affiliation;

    if (selectedProducerId) {
      this.producerDetailsForm.formController?.populateFields({
        ...selectedProducer?.sponsorSpecificData,
        ...selectedProducer,
        agencyName,
      });
    }
  }

  private async _prepareThePageData(formInstance: FormInstanceType): Promise<void> {
    const formAnswers = formInstance.answers.common as Record<string, string>;
    const customFormAnswers = formInstance.answers.custom as Record<string, string>;

    runInAction(() => {
      this._setFormInstance(formInstance);
      this.selectProducerDelegate.populateProducer(formAnswers.id);

      const { id: preSavedProducerId, ...producerAnswers } = (this._formInstance?.answers.common ?? {}) as Record<
        string,
        string
      >;
      const hasPreSavedAnswers = Object.values(producerAnswers).some((answer) => answer.trim() !== '');

      if (hasPreSavedAnswers && !preSavedProducerId) {
        this.selectProducerDelegate.form.populateFields({ producerNotFound: true });
      }
    });

    await this.producerDetailsForm.buildForm(formInstance.id, formAnswers, customFormAnswers);

    // Setup custom component delegates
    runInAction(() => {
      if (this.producerDetailsForm.formController) {
        this.ddarAuthorizedAgentsDelegate = new DDARAuthorizedAgentsDelegate(this.producerDetailsForm.formController);
        this.ddarAdditionalContactsInformationDelegate = new DDARAdditionalContactsInformationDelegate(
          this.producerDetailsForm.formController,
        );
      }
    });
  }

  public async getInitialData(): Promise<void> {
    this.selectProducerDelegate.form.setLoading(true);

    // Get forms & enrollment together for better performance.
    const [, formInstances] = await Promise.all([
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(this._domainParams.periodId),
      getFormInstances(this._domainParams.periodId, FormType.BROKER_FORM),
    ]);
    await this.selectProducerDelegate.requestAvailableProducers();
    await this._prepareThePageData(formInstances[0]);

    runInAction(() => {
      this.selectProducerDelegate.form.setLoading(false);
    });
  }

  public resetPage(): void {
    this.selectProducerDelegate.form.resetForm();
    this.producerDetailsForm.formController?.resetForm();
  }

  public onBackRedirect(): void {
    bpHistory.push(`/checklist/${this._domainParams.periodId}`);
  }

  public async onComplete(): Promise<void> {
    this.producerDetailsForm.formController?.setLoading(true);

    const producerData: Record<string, unknown> = {
      id: this.selectProducerDelegate.selectedProducer?.id,
      ...this.producerDetailsForm.formController?.getCanonicalAnswers(),
    };

    await requestSetAnswers({
      formInstanceId: this._formInstance?.id ?? '',
      answers: {
        common: producerData,
        custom: this.producerDetailsForm.formController!.getCustomFormFieldsObject(),
      },
      complete: true,
    });
    toastManager.addToast('Agent data saved successfully!', ToastColorTypes.SUCCESS);

    await runInAction(async () => {
      await requestApplyUserAction(this._domainParams.periodId, USER_ACTIONS.COMPLETE_BROKER);

      runInAction(() => {
        this.producerDetailsForm.formController?.setLoading(false);
        bpHistory.push(`/checklist/${this._domainParams.periodId}`);
      });
    });
  }

  constructor(domainParams: EnrollmentFlowDomainParams) {
    this._domainParams = domainParams;
    makeAutoObservable(
      this,
      {
        selectProducerDelegate: false,
        producerDetailsForm: false,
      },
      { autoBind: true },
    );

    autorun(() => {
      const selectedProducerId = this.selectProducerDelegate.selectedProducer?.id;

      runInAction(() => {
        this._resetAndPopulateProducerDetailsForm(selectedProducerId);
      });
    });
  }
}

export const DefaultBrokerPageStoreContext = createContext<DefaultBrokerPageStore | null>(null);

export const useDefaultBrokerPageStore = (): DefaultBrokerPageStore => {
  const store = useContext(DefaultBrokerPageStoreContext);
  if (!store) {
    throw new Error('useDefaultBrokerPageStore must be used within DefaultBrokerPageStoreContext.Provider');
  }
  return store;
};
